import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import Sparkline from './Sparkline';
import EmptyState from './EmptyState';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
import { opsColors } from '../../../../theme/opsTokens';

export interface RankedListItem {
  id: string;
  primary: string;
  secondary?: string;
  value: number;
  /** Optional previous-period value for delta indicator. */
  previousValue?: number | null;
  /** Optional sparkline. */
  trend?: number[];
}

interface RankedListProps {
  title: string;
  icon?: React.ReactNode;
  items: RankedListItem[];
  loading?: boolean;
  /** Color hint for sparklines. Defaults to theme primary. */
  color?: string;
  onItemClick?: (item: RankedListItem) => void;
  emptyTitle?: string;
  emptyBody?: string;
  /** Cap rows displayed. Defaults to 10. */
  limit?: number;
  /** Right-column header label. Defaults to "Events". */
  valueLabel?: string;
}

function deltaIndicator(
  current: number,
  previous: number | null | undefined,
  color: { up: string; down: string; flat: string },
  newLabel: string,
) {
  if (previous == null) return null;
  if (previous === 0 && current === 0) return null;
  const pct = previous === 0 ? Infinity : ((current - previous) / Math.abs(previous)) * 100;
  if (!Number.isFinite(pct)) {
    return (
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: color.up }}>
        <Icon size={20} d={ICON_FALLBACKS.ArrowUpward} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{newLabel}</Typography>
      </Stack>
    );
  }
  const sign = pct > 0 ? '+' : '';
  const c = pct === 0 ? color.flat : pct > 0 ? color.up : color.down;
  const trendIconPath = pct === 0 ? ICON_FALLBACKS.Remove : pct > 0 ? ICON_FALLBACKS.ArrowUpward : ICON_FALLBACKS.ArrowDownward;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: c }}>
      <Icon size={16} d={trendIconPath} />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {sign}{pct.toFixed(0)}%
      </Typography>
    </Stack>
  );
}

export default function RankedList({
  title,
  icon,
  items,
  loading = false,
  color,
  onItemClick,
  emptyTitle,
  emptyBody,
  limit = 10,
  valueLabel,
}: RankedListProps) {
  const { t } = useTranslation('admin');
  const sparkColor = color ?? opsColors.brand.primary;
  const visible = items.slice(0, limit);
  const resolvedValueLabel = valueLabel ?? t('analytics.rankedList.events');
  const newLabel = t('analytics.rankedList.new');
  const deltaColors = {
    up: opsColors.semantic.success.color,
    down: opsColors.semantic.error.color,
    flat: opsColors.neutral.inkSoft,
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          </Stack>
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {loading ? (
          <Stack spacing={1}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={32} />
            ))}
          </Stack>
        ) : visible.length === 0 ? (
          <EmptyState compact title={emptyTitle ?? t('analytics.empty.defaultTitle')} body={emptyBody} />
        ) : (
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} sx={{ pb: 0.5, color: 'text.secondary' }}>
              <Typography variant="caption" sx={{ flex: 1 }}>{t('analytics.rankedList.name')}</Typography>
              <Typography variant="caption" sx={{ width: 100, textAlign: 'right' }}>{resolvedValueLabel}</Typography>
            </Stack>
            {visible.map((item, i) => {
              const delta = deltaIndicator(item.value, item.previousValue ?? null, deltaColors, newLabel);
              const interactive = !!onItemClick;
              return (
                <Box
                  key={item.id || `${item.primary}-${i}`}
                  onClick={interactive ? () => onItemClick!(item) : undefined}
                  onKeyDown={interactive ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onItemClick!(item);
                    }
                  } : undefined}
                  tabIndex={interactive ? 0 : undefined}
                  role={interactive ? 'button' : undefined}
                  aria-label={interactive ? t('analytics.rankedList.drillAria', { name: item.primary, value: item.value.toLocaleString(), label: resolvedValueLabel.toLowerCase() }) : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    cursor: interactive ? 'pointer' : 'default',
                    transition: 'background-color 120ms',
                    '&:hover': interactive ? { backgroundColor: 'action.hover' } : undefined,
                    '&:focus-visible': interactive ? { outline: `2px solid ${opsColors.brand.primary}`, outlineOffset: -2 } : undefined,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ width: 22, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {i + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={item.primary} disableHoverListener={item.primary.length < 28} arrow>
                      <Typography
                        variant="body2"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}
                      >
                        {item.primary}
                      </Typography>
                    </Tooltip>
                    {item.secondary && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                      >
                        {item.secondary}
                      </Typography>
                    )}
                  </Box>
                  {item.trend && item.trend.length > 1 && (
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Sparkline values={item.trend} color={sparkColor} width={70} height={24} />
                    </Box>
                  )}
                  <Box sx={{ width: 100, textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {item.value.toLocaleString()}
                    </Typography>
                    {delta && <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{delta}</Box>}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
